import { Images } from '@/assets/images';
import { useSearchFilterParams } from '@/hooks';
import React, { memo, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { isNilOrEmpty, isNotNilOrEmpty } from 'ramda-adjunct';

import Button from '../../button';
import ExcelBatchOperation, {
	TExcelBatchOperation,
} from './excel-batch-operation';
import PageSizeFilter, { TPageSize } from './pagesize-filter';
import SettingColumn, { TSettingColumn } from './setting-column';

export type TActions = {
	onDelete?: (callbackSuccess?: () => void) => void;
	onDeleteSuccessCallback?: (data: any) => void;
	onDeleteFailedCallback?: (error: any) => void;
	deleteBtnHidden?: boolean;
	titleInDetail?: ReactNode;
	actionHidden?: boolean;
	actionsTop?: ReactNode;
	/** @description dont forget `<div className="w-px h-[17px] bg-gray1"></div>` if any */
	actionsLeft?: ReactNode;
	actionsRight?: ReactNode | (() => ReactNode);
	HeaderComponent?: React.ReactNode;
} & TSettingColumn &
	TPageSize &
	TExcelBatchOperation;

const Actions = ({
	onDelete,
	deleteBtnHidden = false,
	titleInDetail,
	actionHidden,
	actionsTop,
	actionsLeft = true,
	actionsRight,
	HeaderComponent,
	...rest
}: TActions) => {
	const [_, setSearchParams] = useSearchFilterParams(rest.tableType || '');

	const { t } = useTranslation('common');

	const _onDelete = useCallback(() => {
		onDelete?.(() => {
			setSearchParams({
				page: 1,
			});
		});
	}, [setSearchParams, onDelete]);

	return (
		<>
			{isNotNilOrEmpty(titleInDetail) && (
				<div className="row-between border-b border-gray1 -mx-5 px-5 mb-5 pb-[15px]">
					<h2 className="font-bold text-[16px] text-black">{titleInDetail}</h2>
					{isNotNilOrEmpty(actionsTop) && (
						<div className="row gap-x-2">
							{actionsTop}
							<PageSizeFilter {...rest} />
						</div>
					)}
				</div>
			)}
			{React.isValidElement(HeaderComponent) && HeaderComponent}
			{!actionHidden && (
				<div className="row-between gap-3 flex-wrap">
					<div className="row gap-x-2.5">
						{isNotNilOrEmpty(actionsLeft) && (
							<>
								{!deleteBtnHidden && (
									<Button
										icon={<img src={Images.iconCloseRed} />}
										onClick={_onDelete}
									>
										{t('delete')}
									</Button>
								)}

								{actionsLeft}
							</>
						)}
					</div>

					<div className="row gap-x-2.5">
						{isNilOrEmpty(titleInDetail) && <PageSizeFilter {...rest} />}
						<SettingColumn {...rest} />
						<ExcelBatchOperation {...rest} />
						{isNotNilOrEmpty(actionsRight) &&
							(React.isValidElement(actionsRight)
								? actionsRight
								: typeof actionsRight === 'function'
									? actionsRight()
									: null)}
					</div>
				</div>
			)}
		</>
	);
};

export default memo(Actions);
